import {computed, inject, Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {forkJoin, map, Observable, of, tap} from 'rxjs';
import {environment} from '../../../environments/environment';
import {PermissionMap} from '../models/types/permission/permission-map.model';

type PreviewUser = {id: number; name: string; email?: string};

export function dashboardPreviewDisplayName(name: string): string {
  return name.trim().replace(/^./u, character => character.toUpperCase());
}

@Injectable({providedIn: 'root'})
export class DashboardPreviewService {
  private http = inject(HttpClient);
  private router = inject(Router);
  readonly targetId = signal<number | null>(this.initialTarget());
  readonly active = computed(() => this.targetId() !== null);
  readonly targetName = signal('');
  readonly permissions = signal<PermissionMap | null>(null);
  readonly allowed = signal(false);
  readonly users = signal<PreviewUser[]>([]);
  readonly viewer = signal<PreviewUser | null>(null);
  private loadedTarget: number | null = null;

  private initialTarget(): number | null {
    const value = new URLSearchParams(window.location.search).get('dashboard_user');
    return value && /^[1-9][0-9]{0,9}$/.test(value) ? Number(value) : null;
  }

  loadUsers(): void {
    this.http.get<{data: {allowed: boolean; users: PreviewUser[]; viewer?: PreviewUser | null}}>(
      environment.baseApi + 'dashboard-preview/employees',
      {headers: {'Skip-Dashboard-Preview': '1'}}
    ).subscribe({
      next: response => {
        this.allowed.set(response.data.allowed);
        const viewer = response.data.viewer;
        this.viewer.set(viewer && Number.isInteger(viewer.id) && viewer.id > 0 && typeof viewer.name === 'string'
          ? {...viewer, name: dashboardPreviewDisplayName(viewer.name)} : null);
        this.users.set((response.data.users ?? []).map(user => ({
          ...user, name: dashboardPreviewDisplayName(user.name),
        })));
        const target = this.users().find(user => user.id === this.targetId());
        if (target) this.targetName.set(target.name);
      },
      error: () => { this.allowed.set(false); this.users.set([]); this.viewer.set(null); },
    });
  }

  prepare(id: number): Observable<boolean> {
    if (this.loadedTarget === id && this.permissions() !== null) return of(true);
    this.targetId.set(id);
    this.targetName.set('');
    this.permissions.set(null);
    return forkJoin({
      context: this.http.post<{data: {subject: PreviewUser}}>(
        environment.baseApiExternal + 'dashboard-preview/context', {}
      ),
      permissions: this.http.get<{data: {permissions: PermissionMap}}>(
        environment.baseApi + 'entity/permissions'
      ),
    }).pipe(tap(result => {
      if (this.targetId() !== id) return;
      this.loadedTarget = id;
      this.targetName.set(this.users().find(user => user.id === id)?.name
        ?? dashboardPreviewDisplayName(result.context.data.subject.name));
      this.permissions.set(result.permissions.data.permissions);
    }), map(() => true));
  }

  clear(): void {
    this.targetId.set(null);
    this.targetName.set('');
    this.permissions.set(null);
    this.loadedTarget = null;
  }

  selectUser(value: string): void {
    if (value === '') {
      void this.router.navigate(['/dashboard'], {queryParams: {dashboard_preview_exit: '1'}});
    } else {
      void this.router.navigate(['/dashboard'], {queryParams: {dashboard_user: value}});
    }
  }

  decorateUrl(url: string): string {
    if (!this.active() || !url) return url;
    const parsed = new URL(url, window.location.origin + '/');
    if (parsed.origin !== window.location.origin || !/^\/dashboard(?:\/|$)/.test(parsed.pathname)) return url;
    parsed.searchParams.set('dashboard_user', String(this.targetId()));
    return parsed.pathname + parsed.search + parsed.hash;
  }

  storageKey(key: string): string {
    return this.active() ? `${key}:dashboard-user-${this.targetId()}` : key;
  }
}
